import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Post } from '../store/posts';

interface CalendarContextMenuProps {
  isOpen: boolean;
  position: { x: number; y: number };
  selectedDate: Date;
  posts: Post[];
  onClose: () => void;
  onNewPost: () => void;
  onNewFlight: () => void;
  onMovePost: (post: Post) => void;
}

export function CalendarContextMenu({
  isOpen,
  position,
  selectedDate,
  posts,
  onClose,
  onNewPost,
  onNewFlight,
  onMovePost,
}: CalendarContextMenuProps) {
  const { t } = useTranslation(['posts', 'flights', 'common']);
  const menuRef = useRef<HTMLDivElement>(null);
  const [showPostPicker, setShowPostPicker] = useState(false);
  const [postSearch, setPostSearch] = useState('');

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        onClose();
        setShowPostPicker(false);
        setPostSearch('');
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, onClose]);

  // Close on escape
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
        setShowPostPicker(false);
        setPostSearch('');
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
    }
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  // Filter posts that can be moved (draft or scheduled)
  const movablePosts = posts.filter(p =>
    ['draft', 'scheduled'].includes(p.status) &&
    p.text // Has some content
  );

  // Filter by search
  const filteredPosts = movablePosts.filter(p => {
    if (!postSearch.trim()) return true;
    const search = postSearch.toLowerCase();
    return p.text?.toLowerCase().includes(search) ||
           p.display_id.toLowerCase().includes(search);
  });

  const formatDate = (date: Date) => {
    return date.toLocaleDateString('de-DE', {
      weekday: 'short',
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  };

  // Adjust position to stay in viewport
  const adjustedPosition = {
    x: Math.min(position.x, window.innerWidth - 280),
    y: Math.min(position.y, window.innerHeight - (showPostPicker ? 400 : 200)),
  };

  return (
    <div
      ref={menuRef}
      className="fixed z-50 bg-white rounded-xl shadow-xl border border-gray-200 py-2 min-w-[240px] animate-in fade-in zoom-in-95 duration-100"
      style={{
        left: adjustedPosition.x,
        top: adjustedPosition.y,
      }}
    >
      {/* Header with date */}
      <div className="px-4 py-2 border-b border-gray-100">
        <p className="text-xs text-gray-500">{t('posts:calendar.date')}</p>
        <p className="font-medium text-gray-900">{formatDate(selectedDate)}</p>
      </div>

      {!showPostPicker ? (
        /* Main Menu */
        <div className="py-1">
          <button
            onClick={() => {
              onNewPost();
              onClose();
            }}
            className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
          >
            <span className="w-8 h-8 rounded-lg bg-blue-100 text-blue-600 flex items-center justify-center">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
            </span>
            <div className="text-left">
              <p className="font-medium">{t('posts:calendar.newPost')}</p>
              <p className="text-xs text-gray-500">{t('posts:calendar.newPostDesc')}</p>
            </div>
          </button>

          <button
            onClick={() => {
              onNewFlight();
              onClose();
            }}
            className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
          >
            <span className="w-8 h-8 rounded-lg bg-purple-100 text-purple-600 flex items-center justify-center">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
            </span>
            <div className="text-left">
              <p className="font-medium">{t('flights:newFlight')}</p>
              <p className="text-xs text-gray-500">{t('flights:newFlightDesc')}</p>
            </div>
          </button>

          {movablePosts.length > 0 && (
            <>
              <div className="border-t border-gray-100 my-1" />
              <button
                onClick={() => setShowPostPicker(true)}
                className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
              >
                <span className="w-8 h-8 rounded-lg bg-amber-100 text-amber-600 flex items-center justify-center">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
                  </svg>
                </span>
                <div className="text-left">
                  <p className="font-medium">{t('posts:calendar.movePost')}</p>
                  <p className="text-xs text-gray-500">{t('posts:calendar.postsAvailable', { count: movablePosts.length })}</p>
                </div>
                <svg className="w-4 h-4 text-gray-400 ml-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </>
          )}
        </div>
      ) : (
        /* Post Picker */
        <div className="py-1">
          <button
            onClick={() => {
              setShowPostPicker(false);
              setPostSearch('');
            }}
            className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-600 hover:bg-gray-50"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            {t('common:actions.back')}
          </button>

          <div className="px-3 py-2">
            <input
              type="text"
              value={postSearch}
              onChange={(e) => setPostSearch(e.target.value)}
              placeholder={t('posts:calendar.searchPost')}
              autoFocus
              className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          <div className="max-h-64 overflow-y-auto">
            {filteredPosts.length === 0 ? (
              <div className="px-4 py-3 text-sm text-gray-500 text-center">
                {t('posts:calendar.noPostsFound')}
              </div>
            ) : (
              filteredPosts.slice(0, 10).map((post) => (
                <button
                  key={post.id}
                  onClick={() => {
                    onMovePost(post);
                    onClose();
                    setShowPostPicker(false);
                    setPostSearch('');
                  }}
                  className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  {post.media_urls && post.media_urls.length > 0 ? (
                    <img
                      src={post.media_urls[0]}
                      alt=""
                      className="w-10 h-10 rounded object-cover flex-shrink-0"
                    />
                  ) : (
                    <div className="w-10 h-10 bg-gray-100 rounded flex items-center justify-center flex-shrink-0">
                      <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                    </div>
                  )}
                  <div className="flex-1 min-w-0 text-left">
                    <p className="font-medium truncate">{post.text?.substring(0, 40) || t('posts:calendar.mediaOnly')}</p>
                    <p className="text-xs text-gray-500">
                      {post.display_id} · {new Date(post.scheduled_for).toLocaleDateString('de-DE')}
                    </p>
                  </div>
                </button>
              ))
            )}
            {filteredPosts.length > 10 && (
              <div className="px-4 py-2 text-xs text-gray-500 text-center border-t border-gray-100">
                {t('posts:calendar.morePosts', { count: filteredPosts.length - 10 })}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
